/**
 * contexts/CustomDomainContext.tsx
 *
 * Detects whether the app is running on a creator's custom domain
 * (e.g. "writings.alice.com" instead of "writevellum.com") and resolves
 * the hostname to a creator handle via the public /api/resolve-domain endpoint.
 *
 * Why this exists:
 *   The frontend is a static nginx SPA. When a creator's custom domain CNAMEs
 *   to writevellum.com, Railway routes the request to the frontend nginx service
 *   which serves index.html for every path. React Router then sees the URL as-is
 *   and has no idea which creator owns the domain.
 *
 *   This context bridges that gap. It runs once on mount, checks the hostname,
 *   and provides { handle, isCustomDomain } to any component that needs it.
 *
 * Usage:
 *   const { handle, isCustomDomain, isLoading } = useCustomDomain();
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../services/api/apiClient';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CustomDomainContextValue {
  /** The creator handle this hostname resolves to, or null if not a custom domain. */
  handle: string | null;
  /** True when the current hostname is a verified creator custom domain. */
  isCustomDomain: boolean;
  /** True while the initial DNS resolution API call is in flight. */
  isLoading: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Hostnames that are native to Vellum — not custom domains. */
const NATIVE_HOSTNAMES = new Set([
  'writevellum.com',
  'www.writevellum.com',
  'localhost',
  '127.0.0.1',
]);

function isNativeHostname(hostname: string): boolean {
  return (
    NATIVE_HOSTNAMES.has(hostname) ||
    hostname.endsWith('.writevellum.com') ||
    // Railway preview URLs (*.up.railway.app) and local dev
    hostname.endsWith('.up.railway.app') ||
    hostname.endsWith('.railway.app')
  );
}

// ── Context ───────────────────────────────────────────────────────────────────

const CustomDomainContext = createContext<CustomDomainContextValue>({
  handle: null,
  isCustomDomain: false,
  isLoading: false,
});

// ── Provider ──────────────────────────────────────────────────────────────────

export function CustomDomainProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const hostname = window.location.hostname.toLowerCase();
  const isCustom = !isNativeHostname(hostname);

  const [handle, setHandle] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(isCustom); // only load if we might need it

  useEffect(() => {
    if (!isCustom) return;

    let cancelled = false;

    apiClient
      .get<{ handle: string | null }>('/resolve-domain', {
        params: { hostname },
      })
      .then((res) => {
        if (!cancelled) setHandle(res.handle ?? null);
      })
      .catch(() => {
        // Resolution failure is non-fatal — the app still renders,
        // just without custom domain routing.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hostname, isCustom]);

  return (
    <CustomDomainContext.Provider
      value={{
        handle,
        isCustomDomain: isCustom && handle !== null,
        isLoading,
      }}
    >
      {children}
    </CustomDomainContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCustomDomain(): CustomDomainContextValue {
  return useContext(CustomDomainContext);
}
